import queryString from 'query-string';

import Config from '../definitions/Config';
import getAffiliateDecoratedURL from './getAffiliateDecoratedURL';
import noticeError from './noticeError';


/**
 * Navigate the browser to a new page, optionally in a new tab.
 *
 * @param {string} url URL of new page
 * @param {Object} options
 * @param {boolean} [options.shouldClearUnload=true] - clear window.onbeforeunload first?
 * @param {boolean} [options.isNewTab=false] - open in new tab?
 * @param {boolean} [options.isRedirect=false] - redirect to new page without adding to history?
 * @param {Object} [options.queryParams=undefined] - object of query params to append to url
 * @param {boolean} [options.shouldUseRedirectNotice=false] - if external site redirects should use the /notify-redirect page
 *
 * @returns {void}
 */

function navigateWindowTo(
    url,
    options = {
        shouldClearUnload: true,
        isNewTab: false,
        isRedirect: false,
        queryParams: undefined,
        shouldUseRedirectNotice: false,
    },
) {
    if (typeof window === 'undefined') {
        throw new Error('[navigateWindowTo.js] Trying to navigate when window is undefined');
    }

    if (!options.isNewTab && options.shouldClearUnload) {
        // We want to make sure the pop-up doesn’t occur if set
        // since the user will be navigated to a new page
        window.onbeforeunload = null;
    }

    // Alter the URL based on whether it's on-site or off-site.
    if (url.startsWith(Config.RATEHUB_BASE_URL) || url.startsWith('/')) {
        // If we’re going to an internal URL include our affiliate query params.
        // append affiliate data to url if one is available (e.g. as a widget); otherwise, the url is unmodified
        url = getAffiliateDecoratedURL(url);
    } else if (Config.ENABLE_REDIRECT_NOTICE_INJECTION && options.shouldUseRedirectNotice) {
        // This is for AMF (mortgage licensing in Quebec) compliance.
        // All off-site redirects must be very explicit & clear to the user.
        // If we're sending them off-site, use our /notify-redirect page.
        url = `${Config.RATEHUB_BASE_URL}/notify-redirect?redirect=${encodeURIComponent(url)}`;
    }

    // If we've been passed query params, inject them.
    if (options.queryParams) {
        const { url: parsedUrl, query } = queryString.parseUrl(url);

        // Mutates `query` by appending or overwriting properties from options.queryParams.
        // Then use modified `query` to construct an updated `url` for navigation.
        Object.entries(options.queryParams).forEach(([ key, value ]) => {
            query[key] = value;
        });

        url = queryString.stringifyUrl({ url: parsedUrl, query });
    }

    if (options.isNewTab) {
        window.open(url, '_blank');
        return;
    }

    // Detecting iframes (https://www.geeksforgeeks.org/how-to-check-a-webpage-is-loaded-inside-an-iframe-or-into-the-browser-window-using-javascript/)
    if (window.self !== window.top) {
        const error = new Error('[navigateWindowTo.js] Trying to navigate from an iframe is not allowed');

        noticeError(error, {
            parentLocation: window.parent.location.href,
            frameLocation: window.location.href,

            targetUrl: url,
        });

        throw error;
    }

    if (options.isRedirect) {
        window.top.location.replace(url);
    } else {
        window.top.location.assign(url);
    }
}

export default navigateWindowTo;
